import { useState, useEffect } from 'react';
import ShoppingListView from './ShoppingListView';
import { loadLists, saveLists } from '../../utils/shoppingLists';

export default function ShoppingListManager() {
  const [lists,      setLists]      = useState(loadLists);
  const [activeId,   setActiveId]   = useState(() => loadLists()[0].id);
  const [renaming,   setRenaming]   = useState(null);   // id of tab being renamed
  const [renameVal,  setRenameVal]  = useState('');

  // Persist whenever lists change
  useEffect(() => { saveLists(lists); }, [lists]);

  const activeList = lists.find(l => l.id === activeId) || lists[0];

  function updateList(updated) {
    setLists(prev => prev.map(l => l.id === updated.id ? updated : l));
  }

  function addList() {
    const newList = { id: crypto.randomUUID(), name: 'New List', items: [] };
    setLists(prev => [...prev, newList]);
    setActiveId(newList.id);
    // immediately enter rename mode
    setRenaming(newList.id);
    setRenameVal('New List');
  }

  function deleteList(id) {
    setLists(prev => {
      const next = prev.filter(l => l.id !== id);
      if (!next.length) {
        const fresh = { id: crypto.randomUUID(), name: 'My List', items: [] };
        setActiveId(fresh.id);
        return [fresh];
      }
      if (id === activeId) setActiveId(next[0].id);
      return next;
    });
  }

  function startRename(list) {
    setRenaming(list.id);
    setRenameVal(list.name);
  }

  function commitRename(id) {
    const name = renameVal.trim() || 'My List';
    setLists(prev => prev.map(l => l.id === id ? { ...l, name } : l));
    setRenaming(null);
  }

  return (
    <div className="slm-wrap">
      {/* Tab bar */}
      <div className="slm-tabs">
        <div className="slm-tabs-scroll">
          {lists.map(list => (
            <div
              key={list.id}
              className={`slm-tab ${list.id === activeId ? 'slm-tab--active' : ''}`}
              onClick={() => setActiveId(list.id)}
            >
              {renaming === list.id ? (
                <input
                  className="slm-tab-input"
                  value={renameVal}
                  autoFocus
                  onChange={e => setRenameVal(e.target.value)}
                  onBlur={() => commitRename(list.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitRename(list.id);
                    if (e.key === 'Escape') setRenaming(null);
                  }}
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <>
                  <span
                    className="slm-tab-name"
                    onDoubleClick={e => { e.stopPropagation(); startRename(list); }}
                  >
                    {list.name}
                  </span>
                  {list.items.length > 0 && (
                    <span className="slm-tab-count">{list.items.filter(i => !i.is_checked).length}</span>
                  )}
                  {lists.length > 1 && (
                    <button
                      className="slm-tab-del"
                      onClick={e => { e.stopPropagation(); deleteList(list.id); }}
                      aria-label={`Delete ${list.name}`}
                    >
                      ✕
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <button className="slm-add-tab" onClick={addList} aria-label="Create a new list" title="Create a new list">
          <span className="slm-add-tab-icon">+</span>
          <span className="slm-add-tab-label">New list</span>
        </button>
      </div>

      {/* Active list */}
      <ShoppingListView
        key={activeList.id}
        list={activeList}
        onUpdate={updateList}
      />
    </div>
  );
}
